import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CurrencyContext = createContext(null);

// Detect currency from browser timezone/locale
function detectUserCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const lang = navigator.language || "en-US";

    // Timezone-based detection
    const tzMap = {
      "Asia/Kolkata": "INR", "Asia/Calcutta": "INR", "Asia/Mumbai": "INR",
      "Asia/Karachi": "PKR", "Asia/Dhaka": "BDT", "Asia/Colombo": "LKR",
      "Asia/Dubai": "AED", "Asia/Riyadh": "SAR", "Asia/Qatar": "QAR",
      "Asia/Kuwait": "KWD", "Asia/Bahrain": "BHD", "Asia/Muscat": "OMR",
      "Asia/Tokyo": "JPY", "Asia/Seoul": "KRW", "Asia/Shanghai": "CNY",
      "Asia/Hong_Kong": "HKD", "Asia/Singapore": "SGD", "Asia/Kuala_Lumpur": "MYR",
      "Asia/Bangkok": "THB", "Asia/Jakarta": "IDR", "Asia/Ho_Chi_Minh": "VND",
      "Asia/Manila": "PHP", "Asia/Taipei": "TWD",
      "Europe/London": "GBP", "Europe/Paris": "EUR", "Europe/Berlin": "EUR",
      "Europe/Rome": "EUR", "Europe/Madrid": "EUR", "Europe/Amsterdam": "EUR",
      "Europe/Istanbul": "TRY", "Europe/Moscow": "RUB",
      "America/New_York": "USD", "America/Chicago": "USD", "America/Los_Angeles": "USD",
      "America/Toronto": "CAD", "America/Vancouver": "CAD",
      "America/Sao_Paulo": "BRL", "America/Mexico_City": "MXN",
      "Australia/Sydney": "AUD", "Australia/Melbourne": "AUD",
      "Pacific/Auckland": "NZD",
      "Africa/Cairo": "EGP", "Africa/Nairobi": "KES", "Africa/Lagos": "NGN",
      "Africa/Johannesburg": "ZAR",
    };

    if (tzMap[tz]) return tzMap[tz];

    // Language-based fallback
    const langMap = { "hi": "INR", "en-IN": "INR", "ur": "PKR", "bn": "BDT", "ar": "AED", "ja": "JPY", "ko": "KRW", "zh": "CNY", "th": "THB", "vi": "VND", "pt-BR": "BRL", "es-MX": "MXN" };
    if (langMap[lang]) return langMap[lang];
    const prefix = lang.split("-")[0];
    if (langMap[prefix]) return langMap[prefix];
  } catch {}
  return "USD";
}

const SYMBOLS = {
  USD: "$", INR: "\u20B9", GBP: "\u00A3", EUR: "\u20AC", AED: "AED ", CAD: "C$", AUD: "A$",
  SGD: "S$", JPY: "\u00A5", KRW: "\u20A9", THB: "\u0E3F", MYR: "RM", IDR: "Rp", VND: "\u20AB",
  PKR: "Rs", BDT: "\u09F3", SAR: "SR", QAR: "QR", BRL: "R$", MXN: "MX$", TRY: "\u20BA",
  ZAR: "R", NGN: "\u20A6", EGP: "E\u00A3", PHP: "\u20B1", CNY: "\u00A5", NZD: "NZ$", HKD: "HK$",
  RUB: "\u20BD", KES: "KSh", LKR: "Rs", NPR: "Rs", CHF: "CHF",
};

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState("USD");
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Detect user currency from browser
    const detected = detectUserCurrency();
    setCurrency(detected);

    // Fetch exchange rates
    axios.get(`${API}/currency/rates`).then(r => {
      setRates(r.data.rates || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const convert = useCallback((usdAmount) => {
    if (!usdAmount || usdAmount === 0) return `${SYMBOLS[currency] || currency + " "}0`;
    if (currency === "USD" || !rates[currency]) return `$${Math.round(usdAmount).toLocaleString()}`;
    const converted = Math.round(usdAmount * rates[currency]);
    const sym = SYMBOLS[currency] || currency + " ";
    return `${sym}${converted.toLocaleString()}`;
  }, [currency, rates]);

  const convertRaw = useCallback((usdAmount) => {
    if (currency === "USD" || !rates[currency]) return usdAmount;
    return Math.round(usdAmount * rates[currency]);
  }, [currency, rates]);

  const symbol = SYMBOLS[currency] || currency + " ";

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, convert, convertRaw, symbol, loading, SYMBOLS }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
